import { useState } from 'react';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

import dayjs from 'dayjs';
import i18next from 'i18next';

import { Notification } from 'types/api/notification.type';

import { notificationDataService } from 'services/notification.service';
import { pdfService } from 'services/pdf.service';

import { routes } from 'data/routes';

import { formatSeasonLabel } from 'helpers/formatSeason';

import { Button } from 'components/Button';
import { Headline } from 'components/Headline';
import { Icon } from 'components/Icon';

import * as S from './Item.styles';

type ItemProps = Notification;

export function Item(props: ItemProps): React.ReactElement {
  const { id, title, createdAt, shortDescription, season, seasonLine, status } = props;
  const { t } = useTranslation();
  const { language } = i18next;

  const [isLoadingPDF, isLoadingPDFSet] = useState<boolean>(false);

  const downloadPDF = () => {
    isLoadingPDFSet(true);

    notificationDataService
      .get(id)
      .then((res) => {
        const lang = language === 'en' ? 'en' : 'it';

        const dataCopy = {
          ...res.data,
          title: res.data?.title[lang],
          shortDescription: res.data?.shortDescription[lang],
          description: res.data?.description[lang],
          createdAt: dayjs(res.data.createdAt).format('DD/MM/YYYY'),
          // eslint-disable-next-line
          products: res.data?.products?.map((item: any) => ({
            ...item,
            description: item.description[lang],
          })),
        };

        pdfService
          .getNotificationPDF(dataCopy)
          .then((resPDF) => {
            const url = window.URL.createObjectURL(new Blob([resPDF.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `${dataCopy?.title || 'notification'}.pdf`);
            document.body.appendChild(link);
            link.click();
            isLoadingPDFSet(false);
          })
          .catch(() => {
            toast.error(t('notification.downloadFailed'));
            isLoadingPDFSet(false);
          });
      })
      .catch(() => {
        toast.error(t('notification.downloadFailed'));
        isLoadingPDFSet(false);
      });
  };

  return (
    <S.Container>
      <S.Info>
        <S.Header>
          <S.IconWrapper>
            <Icon
              fontSize={24}
              name={status === 'READ' ? 'BsEnvelopeOpen' : 'BsEnvelopeFill'}
              color="#1badf8"
            />
          </S.IconWrapper>

          {title ? (
            <Button
              asLink
              to={routes.notificationsDetails(id)}
              scale="xl"
              whiteSpace="normal"
              motive="productTitle"
            >
              {language === 'en' ? title.en : title.it}
            </Button>
          ) : null}
        </S.Header>

        <S.Wrapper>
          {createdAt ? (
            <S.FlexItem>
              <Icon fontSize={11} name="FiCalendar" />

              <Headline scale="s" color="grayLabel">
                {dayjs(createdAt).format('DD/MM/YYYY')}
              </Headline>
            </S.FlexItem>
          ) : null}

          {season ? (
            <S.FlexItem>
              <Headline scale="s" color="grayLabel">
                {formatSeasonLabel(season)}
              </Headline>
            </S.FlexItem>
          ) : undefined}

          {seasonLine ? (
            <S.FlexItem>
              <Headline scale="s" color="grayLabel">
                {seasonLine}
              </Headline>
            </S.FlexItem>
          ) : undefined}
        </S.Wrapper>

        {shortDescription ? (
          <S.Desc>
            <Headline fontFamily="LFT Zeno" scale="s" fontWeight="light" isWrapped={true}>
              {language === 'en' ? shortDescription.en : shortDescription.it}
            </Headline>
          </S.Desc>
        ) : null}
      </S.Info>
      <S.CTA>
        <Button
          asLink
          to={routes.notificationsDetails(id)}
          aria-label={t('notification.read')}
          fontFamily="LFT Zeno"
          scale="xs"
          motive="actionSecondary"
        >
          {t('notification.read')}
        </Button>

        <Button
          isLoading={isLoadingPDF}
          aria-label={t('pdf.download')}
          fontFamily="LFT Zeno"
          scale="xs"
          motive="actionTertiary"
          onClick={() => {
            downloadPDF();
          }}
        >
          {t('pdf.download')}
        </Button>
      </S.CTA>
    </S.Container>
  );
}
